import { parseQueryArguments } from '@/shared'
import {
    ITableListModel,
    TABLE_REQUEST_MODE,
    TableListRequestType,
    countData,
    deleteData,
    getData,
    parseQuery,
    patchData,
    postData,
    postDataOrReason,
} from '../utils/db'

export const MAPPING_MODE = {
    EMPTY: 'empty',
    FIELD: 'field',
}

export type TMappingField = {
    target: string
    type: string
    source: string
}

export type TInTableItem = {
    _id: string
    fileName: string
    name: string
}

export type TMappingItem = {
    _id: string
    name: string
    outTableId: string
    inTables: TInTableItem[]
    mapping: TMappingField[]
}

export default class ConverterListModel implements ITableListModel<TMappingItem> {
    constructor() { }

    public async list({
        mode = TABLE_REQUEST_MODE.PAGINATION,
        project,
        page = 0,
        limit = 10,
        q = '',
        query = {},
    }: TableListRequestType) {
        let strQuery = parseQueryArguments(`page=${page}&limit=${limit}`, arguments)
        if (q) strQuery += '&q=' + encodeURI(q)
        if (mode == TABLE_REQUEST_MODE.POSITION) {
            strQuery += '&mode=position'
        }
        strQuery = parseQuery(strQuery, query)
        return await getData(`/converter/${project}?${strQuery}`)
    }

    public async read(project: string, id: string) {
        return await getData(`/converter/${project}/${id}`)
    }

    public async relatedTables(project: string, id: string) {
        return await getData(`/converter/${project}/${id}/related-tables`)
    }

    public async create(project: string, data: any) {
        return await postData(`/converter/${project}`, data)
    }

    public async update(project: string, id: string, data: any) {
        return await patchData(`/converter/${project}/${id}`, data)
    }

    public async delete(project: string, id: string, options?: any) {
        if (options && Object.keys(options).length > 0) {
            // Send JSON body with delete options
            return await deleteData(`/converter/${project}/${id}`, { data: options })
        }
        return await deleteData(`/converter/${project}/${id}`)
    }

    /** The values a source column holds across the converter's data lists, most frequent first. */
    public async sourceValues(project: string, id: string, source: string) {
        return await getData(`/converter/${project}/${id}/source-values?source=${encodeURIComponent(source)}`)
    }

    /** Queues conversions, one file or several ({ files }); a refused start comes back with its reason. */
    public async convert(project: string, data: any) {
        return await postDataOrReason(`/converter/${project}/convert`, data)
    }

    /** Cancels a waiting or running conversion; a refusal comes back with its reason. */
    public async cancelConvert(project: string, taskId: string) {
        return await postDataOrReason(`/converter/${project}/convert/cancel`, { taskId })
    }

    /** Cancels all conversions of the project the user started himself. */
    public async cancelAllConverts(project: string) {
        return await postDataOrReason(`/converter/${project}/convert/cancel`, { all: true })
    }

    public async count(project: string) {
        return await countData(`/converter/${project}/count`)
    }

    /**
     * What could fill the target fields nobody has assigned yet.
     *
     * Answers with column keys plus the names they stand for. Empty is a
     * perfectly normal answer — nothing is ever invented, so a converter whose
     * fields nobody has assigned before has nothing to offer.
     */
    public async mappingSuggestions(project: string, id: string) {
        return await getData(`/converter/${project}/${id}/mapping-suggestions`, null, true)
    }
}
